import type { CageRecord } from "../../types";

export type IsPenCamera = (cameraId: number) => boolean;

export const penCameraCheck = (
  machinesLoaded: Record<number, CageRecord[]> | undefined,
  machineId: number,
  cageId: number,
): IsPenCamera => {
  const catalog = machinesLoaded?.[machineId]?.find(
    (cage) => cage.cageId === cageId,
  )?.cameras;
  return (cameraId) => !catalog || catalog.includes(cameraId);
};

const removeAt = (
  cameras: readonly number[],
  index: number,
  isPenCamera: IsPenCamera,
): number[] => {
  const rest = cameras.filter((_, i) => i !== index);
  if (isPenCamera(cameras[index]) || index >= rest.length) return rest;
  const displaced = rest[rest.length - 1];
  return [...rest.slice(0, index), displaced, ...rest.slice(index, -1)];
};

export const removeCameras = (
  cameras: readonly number[],
  shouldRemove: (cameraId: number) => boolean,
  isPenCamera: IsPenCamera,
): number[] => {
  let remaining = [...cameras];
  for (
    let index = remaining.findIndex(shouldRemove);
    index !== -1;
    index = remaining.findIndex(shouldRemove)
  ) {
    remaining = removeAt(remaining, index, isPenCamera);
  }
  return remaining;
};

export const placeCameraInSlot = (
  cameras: readonly number[],
  source: number,
  target: number,
  isPenCamera: IsPenCamera,
): number[] => {
  const sourceIndex = cameras.indexOf(source);
  if (sourceIndex === -1) {
    throw new Error(`Source camera ${source} is not in the cell`);
  }
  if (source === target) return [...cameras];

  const targetIndex = cameras.indexOf(target);
  const placed = [...cameras];
  placed[sourceIndex] = target;

  if (targetIndex !== -1) {
    placed[targetIndex] = source;
    return isPenCamera(source)
      ? placed
      : removeAt(placed, targetIndex, isPenCamera);
  }

  return isPenCamera(source) ? [...placed, source] : placed;
};

export const cycleCellCameras = (
  cameras: readonly number[],
  isPenCamera: IsPenCamera,
): number[] | undefined => {
  if (!cameras.every(isPenCamera)) {
    const penCameras = removeCameras(
      cameras,
      (camera) => !isPenCamera(camera),
      isPenCamera,
    );
    return penCameras.length > 0 ? penCameras : undefined;
  }
  if (cameras.length <= 1) return undefined;

  const [first, ...rest] = cameras;
  return [...rest, first];
};
